import { useWeather } from 'common';
import { useTranslation } from 'react-i18next';
import { tokens } from 'tamagui-config';

import { useGetCurrentLocation } from './useGetCurrentLocation';
import { UvIndex, WeatherData, WeatherIconName } from '../../types/Weather';

export const useGetWeatherData = () => {
  const { t } = useTranslation();
  const { currentLocation } = useGetCurrentLocation();
  const { fetchWeather } = useWeather();

  const mapTemperature = ({ degrees, unit }: { degrees: number; unit: string }) =>
    `${Math.round(degrees)}°${unit === 'FAHRENHEIT' ? 'F' : 'C'}`;

  const mapWeatherConditionToIcon = (condition: string): WeatherIconName => {
    switch (condition) {
      case 'Sunny':
        return 'sun';
      case 'Cloudy':
        return 'cloud';
      case 'Rainy':
        return 'rain';
      case 'Thunderstorm':
        return 'thunderstorm';
      case 'Windy':
        return 'wind';
      default:
        return 'partlyCloud';
    }
  };

  const mapUvIndex = (uvIndex: number): UvIndex => {
    if (uvIndex < 3) {
      return {
        value: uvIndex,
        level: t('weather.uvIndexLow'),
        message: t('weather.uvIndexLowMessage'),
        iconName: 'sun' as WeatherIconName,
        glowColor: tokens.color.goodGreen,
      };
    }
    if (uvIndex < 6) {
      return {
        value: uvIndex,
        level: t('weather.uvIndexModerate'),
        message: t('weather.uvIndexModerateMessage'),
        iconName: 'sun' as WeatherIconName,
        glowColor: tokens.color.goodGreen,
      };
    }
    if (uvIndex < 8) {
      return {
        value: uvIndex,
        level: t('weather.uvIndexHigh'),
        message: t('weather.uvIndexHighMessage'),
        iconName: 'sun' as WeatherIconName,
        glowColor: tokens.color.glowOrange,
      };
    }
    if (uvIndex < 11) {
      return {
        value: uvIndex,
        level: t('weather.uvIndexVeryHigh'),
        message: t('weather.uvIndexVeryHighMessage'),
        iconName: 'sun' as WeatherIconName,
        glowColor: tokens.color.glowRed,
      };
    }
    return {
      value: uvIndex,
      level: t('weather.uvIndexExtreme'),
      message: t('weather.uvIndexExtremeMessage'),
      iconName: 'sun' as WeatherIconName,
      glowColor: tokens.color.glowRed,
    };
  };

  const getWeather = async (): Promise<WeatherData> => {
    const weatherData = await fetchWeather(currentLocation.lat, currentLocation.long);
    return {
      todayHighLow: {
        iconName: mapWeatherConditionToIcon(weatherData.current.condition),
        temperature: mapTemperature(weatherData.current.temperature),
        condition: weatherData.current.condition,
        low: {
          value: mapTemperature(weatherData.current.lowTemperature),
        },
        high: {
          value: mapTemperature(weatherData.current.highTemperature),
        },
      },
      upcoming: {
        temperature: mapTemperature(weatherData.upcoming.temperature),
        condition: weatherData.upcoming.condition,
        forecast: weatherData.upcoming.forecast.map(
          (day: {
            date: string | number | Date;
            condition: string;
            temperature: { degrees: number; unit: string };
          }) => ({
            day: new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' }),
            iconName: mapWeatherConditionToIcon(day.condition),
            temp: mapTemperature(day.temperature),
          }),
        ),
      },
      uvIndex: mapUvIndex(weatherData.current.uvIndex),
    };
  };

  return {
    getWeather,
  };
};
